import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../layout/DefaultLayout';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import authService from '../services/authService';
import userService from '../services/userService';
import { useGlobalState } from '../state';

import STORAGE_IMG_PLACEHOLDER from '../images/profile.jpg';
import LEY_IMG from'../images/ley.png';
import LOGO_IMG from '../images/logo_nuevo.png';
import Breadcrumb from '../components/Breadcrumbs/Breadcrumb';

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const Credencial: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(false);
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [titulo, setTitulo] = useState<string | null>(null);
  const [nombreColegio, setNombreColegio] = useState<string | null>(null);
  const [codigoColegio, setCodigoColegio] = useState<string | null>(null);
  const [numColegio, setNumColegio] = useState<string | null>(null);
  const [tomo, setTomo] = useState<string | null>(null);
  const [folio, setFolio] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const load = async () => {
      try {
        if (user === undefined) return; // auth still checking
        if (!user) {
          navigate('/auth/signin');
          return;
        }

        setLoading(true);
        
        // Try to get profile picture
        try {
            const resp: any = await userService.getProfilePicture(user.datosUsuario.legajoPrevisional);
          // resp may be { data: base64 } or an ArrayBuffer depending on service implementation
          if (!resp) {
            setImageUri(null);
          } else if (typeof resp === 'string') {
            // assume already base64 string
            setImageUri(resp.startsWith('data:') ? resp : `data:image/jpeg;base64,${resp}`);
          } else if (resp.data && typeof resp.data === 'string') {
            setImageUri(resp.data.startsWith('data:') ? resp.data : `data:image/jpeg;base64,${resp.data}`);
          } else if (resp instanceof ArrayBuffer || resp.buffer instanceof ArrayBuffer) {
            const buffer = resp instanceof ArrayBuffer ? resp : resp.buffer;
            const base64 = arrayBufferToBase64(buffer);
            setImageUri(`data:image/jpeg;base64,${base64}`);
          } else {
            // fallback
            setImageUri(null);
          }
        } catch (imgErr) {
          // ignore image errors, show placeholder
          setImageUri(null);
        }

        // Fill textual fields from user or fetch details if missing
        if (user && (!user.datosUsuario.titulo || !user.datosUsuario.nombreColegio)) {
          try {
            const res: any = await userService.getDatosDinamicos(false);
              if (res?.success) {
                
              const afiliado = res.data?.afiliado ?? res.data;
              if (afiliado) {
                setTitulo(afiliado.titulo ?? afiliado?.Titulo ?? null);
                setTomo(afiliado.tomo ?? afiliado?.Tomo ?? null);
                setFolio(afiliado.folio ?? afiliado?.Folio ?? null);
                setCodigoColegio(afiliado.codigoColegioActual ?? afiliado?.CodigoColegioActual ?? null);
                setNumColegio(afiliado.numCol ?? afiliado?.NumCol ?? null);
                setNombreColegio(afiliado.nombreColegio ?? afiliado?.NombreColegio ?? null);
              }
            } else {
              // don't fail the page; just show what's available
            }
          } catch (e) {
            // ignore
          }
        } else if (user) {
          setTitulo(user.datosUsuario.titulo ?? null);
          setTomo(user.datosUsuario.tomo ?? null);
          setFolio(user.datosUsuario.folio ?? null);
          setCodigoColegio(user.datosUsuario.codigoColegioActual ?? null);
          setNumColegio(user.datosUsuario.numCol ?? null);
          setNombreColegio(user.datosUsuario.nombreColegio ?? null);
        }
      } catch (ex: any) {
        setError('No se pudo cargar la credencial. Intenta nuevamente.');
        console.error(ex);
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
      <DefaultLayout>
          <Breadcrumb pageName="Credencial Digital" />

          {error && <div className="mt-4"><Alert type="failure" message={error} /></div>}
          
          <div className="max-w-6xl md:mx-auto bg-white md:p-6 justify-center ">    
              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                  <div className="flex items-start w-full md:w-[430px] mx-auto">
                      <div className="flex items-start border rounded-lg border">
                          <div className="w-1/2">
                              <div className="rounded-tl-lg rounded-bl-lg overflow-hidden bg-transparent">
                                  {loading ? (
                                      <div className="flex items-center justify-center h-48 bg-gray-100">
                                          <Loading statusText="Cargando" />
                                      </div>
                                  ) : (
                                      <img
                                          src={imageUri ?? STORAGE_IMG_PLACEHOLDER}
                                          alt="Foto perfil"
                                          className="w-full h-48 object-cover"
                                      />
                                  )}
                              </div>
                          </div>

                          <div className="bg-primario-azul-400 flex items-center justify-center">
                              <img src={LEY_IMG} alt="Ley" style={{ width: 61 }} />
                          </div>

                          <div className="w-5/12 bg-gray-50 rounded-tr-lg rounded-br-lg flex items-center justify-center p-4">
                              <img src={LOGO_IMG} alt="Logo" className="w-36 h-auto object-contain" />
                          </div>
                      </div>

                  </div>

                  <div className="bg-gray-100 rounded-lg p-4 mt-6">
                      <div className="flex items-center justify-between mb-4">
                          <div className="text-lg font-semibold">{nombreColegio ?? 'No Disponible'}</div>
                          {codigoColegio && (
                              <img
                                  src={`${process.env.REACT_APP_API_URL ?? ''}/assets/logos/${codigoColegio}.jpg`}
                                  alt="Logo colegio"
                                  className="h-24 w-24 object-contain rounded"
                              />
                          )}
                      </div>

                      <div className="border-t border-gray-200 my-3" />

                      <div className="grid grid-cols-2 gap-3">
                          <div className="text-sm text-gray-600">Título</div>
                          <div className="text-sm font-semibold">{titulo ?? 'No Disponible'}</div>

                          <div className="text-sm text-gray-600">Nombre</div>
                          <div className="text-sm font-semibold">{`${user?.datosUsuario.apellido ?? ''} ${user?.datosUsuario.nombre ?? ''}`.trim()}</div>

                          <div className="text-sm text-gray-600">Documento (DNI)</div>
                          <div className="text-sm font-semibold">{user?.datosUsuario.dni ?? '—'}</div>

                          <div className="text-sm text-gray-600">Estado</div>
                          <div className="text-sm font-semibold">Activo</div>

                          <div className="text-sm text-gray-600">Tomo</div>
                          <div className="text-sm font-semibold">{tomo ?? '—'}</div>

                          <div className="text-sm text-gray-600">Folio</div>
                          <div className="text-sm font-semibold">{folio ?? '—'}</div>

                          <div className="text-sm text-gray-600">Leg. Col.</div>
                          <div className="text-sm font-semibold">{numColegio ?? '—'}</div>

                          <div className="text-sm text-gray-600">Leg. Prev.</div>
                          <div className="text-sm font-semibold">{user?.datosUsuario.legajoPrevisional ?? '—'}</div>
                      </div>
                  </div>
              </div>                             
          </div>

          <div className="mt-6 flex justify-center gap-3">
              <button
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 rounded border border-gray-300 bg-white"
              >
                  Volver
              </button>
          </div>
    </DefaultLayout>
  );
};

export default Credencial;